import { useEffect } from 'react';

const SITE_NAME = 'Navyrx Sports';
const SITE_URL = import.meta.env.VITE_SITE_URL ?? 'https://navyrx.com';
const DEFAULT_DESCRIPTION =
  'AI-powered sports intelligence and decision-support platform with transparent recommendations and risk-aware workflows.';

interface PageMetaInput {
  title?: string;
  description?: string;
  path?: string;
  structuredData?: Record<string, unknown> | null;
}

function upsertMeta(selector: string, create: () => HTMLMetaElement, content: string) {
  const existing = document.querySelector(selector) as HTMLMetaElement | null;
  const node = existing ?? create();
  node.content = content;
  if (!existing) document.head.appendChild(node);
}

export function usePageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  structuredData = null,
}: PageMetaInput) {
  useEffect(() => {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const absoluteUrl = `${SITE_URL}${normalizedPath}`;

    document.title = `${title} | ${SITE_NAME}`;

    upsertMeta(
      'meta[name="description"]',
      () => {
        const meta = document.createElement('meta');
        meta.name = 'description';
        return meta;
      },
      description,
    );

    upsertMeta(
      'meta[property="og:title"]',
      () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:title');
        return meta;
      },
      `${title} | ${SITE_NAME}`,
    );

    upsertMeta(
      'meta[property="og:description"]',
      () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:description');
        return meta;
      },
      description,
    );

    upsertMeta(
      'meta[property="og:type"]',
      () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:type');
        return meta;
      },
      'website',
    );

    upsertMeta(
      'meta[property="og:url"]',
      () => {
        const meta = document.createElement('meta');
        meta.setAttribute('property', 'og:url');
        return meta;
      },
      absoluteUrl,
    );

    upsertMeta(
      'meta[name="twitter:card"]',
      () => {
        const meta = document.createElement('meta');
        meta.name = 'twitter:card';
        return meta;
      },
      'summary_large_image',
    );

    const canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (canonical) {
      canonical.href = absoluteUrl;
    } else {
      const link = document.createElement('link');
      link.rel = 'canonical';
      link.href = absoluteUrl;
      document.head.appendChild(link);
    }

    const jsonLdId = 'navyrx-structured-data';
    const existingJsonLd = document.getElementById(jsonLdId) as HTMLScriptElement | null;
    if (structuredData) {
      const jsonLd = existingJsonLd ?? document.createElement('script');
      jsonLd.id = jsonLdId;
      jsonLd.type = 'application/ld+json';
      jsonLd.text = JSON.stringify(structuredData);
      if (!existingJsonLd) document.head.appendChild(jsonLd);
    } else if (existingJsonLd) {
      existingJsonLd.remove();
    }
  }, [description, path, structuredData, title]);
}
